import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, ViewChild } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import {
    MatLegacyDialog as MatDialog,
    MatLegacyDialogConfig as MatDialogConfig,
    MatLegacyDialogModule as MatDialogModule,
    MatLegacyDialogRef as MatDialogRef,
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { Router } from '@angular/router';
import { DataFromCSV } from '@dave/types';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { FileEntity } from '../../dave-data-module/entities/file.entity';
import { LedgerImportDocumentTypeNames, LedgerImportDocumentTypes, LedgerImportStatusEnum, LedgerImportStatusNames, ledgerImportStatusValues } from '../../dave-data-module/entities/ledger-import.entity';
import { AccountsReceivableLedgerResolver } from '../../dave-data-module/guards/accounts-receivable-ledger.resolver';
import { CommissionResolver } from '../../dave-data-module/guards/commission.resolver';
import { LedgerImportResolver } from '../../dave-data-module/guards/ledger-import.resolver';
import { QuantityTypeResolver } from '../../dave-data-module/guards/quantity-type.resolver';
import { UserResolver } from '../../dave-data-module/guards/user.resolver';
import { getFetched$ } from '../../dave-data-module/helper/helper';
import { MaterialDataService } from '../../dave-data-module/services/material-data.service';
import { State } from '../../dave-data-module/State';
import { fileUploadParamsObj } from '../../dave-data-module/State/actions/files.actions';
import { getAccountsReceivableLedgerDictionary, getAccountsReceivableLedgersFetched, getQuantityTypesFetched } from '../../dave-data-module/State/selectors/accounting.selector';
import { getCommissionsActive, getCommissionsFetched } from '../../dave-data-module/State/selectors/commission.selector';
import { getLedgerImportById, getLedgerImports, getLedgerImportsFetched } from '../../dave-data-module/State/selectors/ledger-import.selector';
import { getUserById, getUsersFetched } from '../../dave-data-module/State/selectors/users.selectors';
import { DaveLoadingModule } from '../../dave-loading/dave-loading.module';
import { _getArlChildren } from '../../dave-reports/components/arl-calculation/arl-calculation.helper';
import { AppButtonModule } from '../../dave-utils-module/app-button-module/app-button.module';
import { AppFilterModule } from '../../dave-utils-module/app-filter-module/app-filter.module';
import { FilterOption, FILTER_TYPE_SEARCH_MULTI_SELECT, IFilterTypeSearchMultiSelectValue } from '../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { DropzoneComponent } from '../../dave-utils-module/dave-shared-components-module/components/dropzone/dropzone.component';
import { appMatDialogDefaultConfig, isNotNullOrUndefined } from '../../helper/helper';
import { AllReportsMeta, InvoiceEditorMeta, ReportsPageMeta } from '../../helper/page-metadata';
import { CustomLabelService } from '../../services/custom-label/custom-label.service';
import { FilterTypes } from '../../services/default-filter.service';
import {
    ConfirmWarehouseMovementDialogComponent,
    ConfirmWarehouseMovementDialogComponentDialogData,
    ConfirmWarehouseMovementDialogComponentDialogReturnData,
} from './confirm-warehouse-movement-dialog/confirm-warehouse-movement-dialog.component';
import { MaterialForWarehouseMovementListComponent } from './material-for-warehouse-movement-list/material-for-warehouse-movement-list.component';
import {
    SelectWarehouseMovementTypeDialogComponent,
    SelectWarehouseMovementTypeDialogComponentDialogData,
    SelectWarehouseMovementTypeDialogComponentDialogReturnData,
} from './select-warehouse-movement-type-dialog/select-warehouse-movement-type-dialog.component';
import { WarehouseMovementLedgerImportSelectTableComponent } from './warehouse-movement-ledger-import-select-table/warehouse-movement-ledger-import-select-table.component';
import { getPartner, getPartnerFetched } from '../../dave-data-module/State/selectors/partners.selectors';
import { ToastrService } from 'ngx-toastr';
import { MaterialAmountType } from '@dave/types/dist/proto/erp/warehouseMovements';
import { ArlDataService } from '../../dave-data-module/services/arl-data.service';

interface ledgerImportFilter {
    [FilterTypes.CommissionId]: IFilterTypeSearchMultiSelectValue<number>[];
    [FilterTypes.Status]: IFilterTypeSearchMultiSelectValue<LedgerImportStatusEnum>[];
}
export interface WarehouseMovementComponentDialogData {
    defaultAddedMaterials?:{materialId: number, warehouseMovementType: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput}[];
    defaultWarehouseMovementType?: 'in' | 'out';
}

@Component({
    selector: 'app-warehouse-movement',
    standalone: true,
    imports: [
        CommonModule,
        MaterialForWarehouseMovementListComponent,
        AppButtonModule,
        MatDialogModule,
        DaveLoadingModule,
        FontAwesomeModule,
        MatTooltipModule,
        AppFilterModule,
        MatMenuModule,
        MatBadgeModule,
        WarehouseMovementLedgerImportSelectTableComponent,
        DropzoneComponent,
    ],
    templateUrl: './warehouse-movement.component.html',
    styleUrls: ['./warehouse-movement.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseMovementComponent {
    public static DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
        width: '80vw',
        height: '80vh',
    };
    @ViewChild(MaterialForWarehouseMovementListComponent) private materialListComponent: MaterialForWarehouseMovementListComponent;
    @ViewChild(WarehouseMovementLedgerImportSelectTableComponent) private ledgerImportListComponent: WarehouseMovementLedgerImportSelectTableComponent;
    public readonly uploadUrl = environment.gatewayApiUrl + '/path/to/fileUpload';
    protected selectedLedgerImportId$ = new BehaviorSubject<number | null>(null);
    protected startValuesForMaterialWarehouseMovementList: { Quantity: number; BookingText: string; Information: string; MaterialId: number; arlId?: number; QuantityTypeId: number; CommissionId: number | null; warehouseMovementType: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput; detectedCommissionNumber: string}[] = null;
    protected selectedcsv$ = new BehaviorSubject<DataFromCSV[] | null>(null);
    protected selectedWareHouseMovementType$ = new BehaviorSubject<'in' | 'out' | null>(null);
    protected headline$: Observable<string>;
    protected selectedWareHouseMovementTypeLabel$: Observable<string>;
    protected ledgerImportAuthor$: Observable<string>;
    protected ledgerImportCommissionNumber$: Observable<string>;
    protected selectableLedgerImports$: Observable<number[]>;
    protected loading$: Observable<boolean>;
    protected defaultAddedMaterials: {materialId: number, warehouseMovementType: MaterialAmountType.MaterialAmountTypeInput | MaterialAmountType.MaterialAmountTypeOutput}[] = null;
    protected hideBackButton: boolean;
    protected showFileUpload$: Observable<boolean>;
    protected ledgerImportFilterValues$: BehaviorSubject<ledgerImportFilter>;
    protected ledgerImportFilterAmount$: Observable<number>;
    protected ledgerImportFilterSettings$: Observable<FilterOption[]>;
    fileUploadParams: fileUploadParamsObj = {
        hidden: 'true',
    };

    constructor(
        private store: Store<State>,
        ledgerImportResolver: LedgerImportResolver,
        arlResolver: AccountsReceivableLedgerResolver,
        quantityTypeResolver: QuantityTypeResolver,
        userResolver: UserResolver,
        commissionResolver: CommissionResolver,
        cls: CustomLabelService,
        private dialogRef: MatDialogRef<WarehouseMovementComponent>,
        private dialog: MatDialog,
        private router: Router,
        private materialDataService: MaterialDataService,
        @Inject(MAT_DIALOG_DATA) dialogData: WarehouseMovementComponentDialogData,
        private toastr: ToastrService,
        protected arlDataService: ArlDataService
    ) {
        firstValueFrom(store.select(getLedgerImportsFetched)).then((fetched) => {
            if (!fetched) {
                ledgerImportResolver.resolve();
            }
        });
        firstValueFrom(store.select(getAccountsReceivableLedgersFetched)).then((fetched) => {
            if (!fetched) {
                arlResolver.resolve();
            }
        });
        firstValueFrom(store.select(getQuantityTypesFetched)).then((fetched) => {
            if (!fetched) {
                quantityTypeResolver.resolve();
            }
        });
        firstValueFrom(store.select(getUsersFetched)).then((fetched) => {
            if (!fetched) {
                userResolver.resolve();
            }
        });
        firstValueFrom(store.select(getCommissionsFetched)).then((fetched) => {
            if (!fetched) {
                commissionResolver.resolve();
            }
        });
        if (dialogData?.defaultWarehouseMovementType) {
            this.selectWareHouseMovementType(dialogData.defaultWarehouseMovementType);
        }
        if (dialogData?.defaultAddedMaterials) {
            this.defaultAddedMaterials = dialogData.defaultAddedMaterials;
        }
        this.hideBackButton = dialogData && dialogData.defaultAddedMaterials.length > 0 && !!dialogData.defaultWarehouseMovementType;

        const ledgerImport$ = this.selectedLedgerImportId$.pipe(
            switchMap((selectedLedgerImportId) => {
                return selectedLedgerImportId ? getFetched$(store, getLedgerImportsFetched, getLedgerImportById({ id: selectedLedgerImportId })) : of(null);
            }),
        );
        this.ledgerImportAuthor$ = ledgerImport$.pipe(switchMap((li) => (li?.UserId ? getFetched$(store, getUsersFetched, getUserById({ id: li.UserId })).pipe(map((user) => user?.DisplayName)) : of(null))));
        this.ledgerImportCommissionNumber$ = ledgerImport$.pipe(map((li) => li?.CommissionNumber));
        this.headline$ = ledgerImport$.pipe(switchMap((li) => (li ? of(LedgerImportDocumentTypeNames.get(li.DocumentType) + ' ' + li.ConsolidatedInvoiceId) : this.selectedcsv$.pipe(map(s => s ? 'Handscanner' : 'Dokument auswählen')))));
        this.selectedWareHouseMovementTypeLabel$ = this.selectedWareHouseMovementType$.pipe(
            map((v) => {
                if (v === 'out') {
                    return 'Warenausgang';
                } else if (v === 'in') {
                    return 'Wareneingang';
                }
                return null;
            }),
        );
        this.selectableLedgerImports$ = getFetched$(store, getLedgerImportsFetched, getLedgerImports).pipe(
            map((ledgerImports) => ledgerImports.filter((li) => [LedgerImportDocumentTypes.Order, LedgerImportDocumentTypes.DeliveryNote].includes(li.DocumentType))),
            switchMap((ledgerImports) =>
                this.ledgerImportFilterValues$.pipe(
                    map((filter) => {
                        let filtered = ledgerImports.slice();
                        if (filter[FilterTypes.CommissionId]?.length) {
                            filtered = filtered.filter((li) => filter[FilterTypes.CommissionId].some((v) => v.id === li.CommissionId));
                        }
                        if (filter[FilterTypes.Status]?.length) {
                            filtered = filtered.filter((li) => filter[FilterTypes.Status].some((v) => v.id === li.Status));
                        }
                        return filtered;
                    }),
                ),
            ),
            map((ledgerImports) => ledgerImports.map((li) => li.Id)),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.loading$ = this.selectableLedgerImports$.pipe(
            map((v) => !v),
            startWith(true),
        );

        this.ledgerImportFilterValues$ = new BehaviorSubject({
            [FilterTypes.CommissionId]: [],
            [FilterTypes.Status]: [],
        });
        this.ledgerImportFilterAmount$ = this.ledgerImportFilterValues$.pipe(
            map((f) => {
                let filterCount = 0;
                if (f[FilterTypes.CommissionId]?.length) {
                    filterCount++;
                }
                if (f[FilterTypes.Status]?.length) {
                    filterCount++;
                }
                return filterCount;
            }),
        );
        this.ledgerImportFilterSettings$ = combineLatest([getFetched$(store, getCommissionsFetched, getCommissionsActive), cls.getMultiple$('Commission')]).pipe(
            map(([commissions, commissionLabel]) => {
                return [
                    {
                        Name: FilterTypes.CommissionId,
                        Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                        Label: commissionLabel,
                        Values: commissions.map((c) => ({
                            label: c.optionLabel,
                            id: c.Id,
                        })),
                    },
                    {
                        Name: FilterTypes.Status,
                        Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                        Label: 'Status',
                        Values: ledgerImportStatusValues.map((s) => ({
                            label: LedgerImportStatusNames.get(s),
                            id: s,
                        })),
                    },
                ];
            }),
        );
        this.showFileUpload$ = store.select(getPartner).pipe(map(p => p.Id === window['env']['luksch_partner_id'] || p.Id === window['env']['luksch_partner_id2']))
    }
    selectedLedgerImportChange(number: number) {
        if (number) {
            const ledgerImportArlsHot$ = this.arlDataService.getARLsForLedgerImports$([number]);
            const dialogRef = this.dialog
                .open<SelectWarehouseMovementTypeDialogComponent, SelectWarehouseMovementTypeDialogComponentDialogData, SelectWarehouseMovementTypeDialogComponentDialogReturnData>(SelectWarehouseMovementTypeDialogComponent, {
                    ...SelectWarehouseMovementTypeDialogComponent.DefaultConfig,
                });

            combineLatest([ledgerImportArlsHot$, dialogRef.afterClosed()]).pipe(first()).subscribe(([arls, ret]) => {
                if (ret) {
                    firstValueFrom(getFetched$(this.store, getLedgerImportsFetched, getLedgerImportById({ id: number }))).then((ledgerImport) => {
                        const arlsFromLedgerImport = ledgerImport ? ledgerImport.AccountsReceivableLedgerIds.map((arlId) => _getArlChildren(arlId, arls, true)).flat() : [];
                        this.selectedcsv$.next(null);
                        this.startValuesForMaterialWarehouseMovementList = arlsFromLedgerImport.map((arl) => ({ ...arl, arlId: arl.Id, warehouseMovementType: ret === 'in' ? MaterialAmountType.MaterialAmountTypeInput : MaterialAmountType.MaterialAmountTypeOutput, detectedCommissionNumber: null }));
                        this.selectedLedgerImportId$.next(number);
                        this.selectWareHouseMovementType(ret);
                    });
                } else {
                    this.ledgerImportListComponent?.selectAll(false);
                    this.selectedLedgerImportId$.next(null);
                }
            });
        } else {
            this.selectedLedgerImportId$.next(null);
        }
    }

    selectWareHouseMovementType(out: 'in' | 'out' | null) {
        this.selectedWareHouseMovementType$.next(out);
    }

    async onConfirmClick() {
        if (!this.materialListComponent) {
            return;
        }
        const materials = await this.materialListComponent.getCurrentMaterialData();
        const ledgerImportId = this.selectedLedgerImportId$.value;
        let commissionId: number;
        if (ledgerImportId) {
            const ledgerImport = await firstValueFrom(getFetched$(this.store, getLedgerImportsFetched, getLedgerImportById({ id: ledgerImportId })));
            commissionId = ledgerImport?.CommissionId;
        }
        let customerId: number;
        if (this.selectedcsv$.value) {
            const partner = await firstValueFrom(getFetched$(this.store, getPartnerFetched, getPartner));
            customerId = partner?.Settings?.customerIdForLedgerImportsFromWarehouseMovementCSVImport
        }
        this.dialog
            .open<ConfirmWarehouseMovementDialogComponent, ConfirmWarehouseMovementDialogComponentDialogData, ConfirmWarehouseMovementDialogComponentDialogReturnData>(ConfirmWarehouseMovementDialogComponent, {
                ...ConfirmWarehouseMovementDialogComponent.DefaultConfig,
                data: {
                    defaultCommissionId: commissionId,
                    // warehouseMovementType: this.selectedWareHouseMovementType$.value,
                    materials,
                    customerIdForNewLedgerImports: customerId,
                    ledgerImportId,
                },
            })
            .afterClosed()
            .subscribe((ret) => {
                if (ret?.created) {
                    if (ret.routeToDeliveryNote && ret.deliveryNote) {
                        this.router.navigate(['/', ReportsPageMeta.Path, AllReportsMeta.Path, ret.deliveryNote]);
                    }
                    this.dialogRef?.close();
                }
            });
    }

    onDropzoneQueueComplete($event: void) {}

    onDropzoneUploadSuccess(files: FileEntity[]) {
        console.log('onDropzoneUploadSuccess', files);
        if (files?.length === 1) {
            const file = files[0];
            this.materialDataService
                .getWarehouseMovementFromCSV({ VersionId: file.GetLastVersion().Id.toString() })
                .then((res) => {
                    console.log(res);
                    if (res) {
                        if (res.length) {
                            this.startValuesForMaterialWarehouseMovementList = res.map(r =>{
                                if (!r.Type) {
                                    throw 'Type fehlt'
                                }
                                return {
                                    arlId: null,
                                    Quantity: r.Amount,
                                    BookingText: r.ArticleNumber,
                                    Information: r.Description,
                                    MaterialId: isNotNullOrUndefined(r.MaterialId) ? +r.MaterialId : null,
                                    QuantityTypeId: isNotNullOrUndefined(r.QuantityType) ? +r.QuantityType : null,
                                    CommissionId: isNotNullOrUndefined(r.CommissionId) ? +r.CommissionId : null,
                                    warehouseMovementType: r.Type === 'RSP' ? MaterialAmountType.MaterialAmountTypeInput : r.Type === 'LSP' ? MaterialAmountType.MaterialAmountTypeOutput : null,
                                    detectedCommissionNumber: r.ProjectNumber,
                                }
                            })
                            console.log('missing', res.filter(r => !r.MaterialId))

                            this.selectedcsv$.next(res);
                        } else {
                            this.toastr.info('Keine Materialien gefunden')
                        }
                    } else {
                        this.toastr.error('Datei konnte nicht eingelesen werden')
                    }
                })
                .catch((err) => {
                    console.log(err);
                    //todo delete files
                });
        }
    }
}
